import { Controller, useFieldArray, useWatch, type Control, type FieldErrors, type UseFormRegister } from 'react-hook-form';
import { Anchor, Copy, Plus, Trash2 } from 'lucide-react';
import { Button, Checkbox, DateField, EmptyState, FormField, IconButton, NumberField, TextField } from '@/components';
import { useCodeValues } from '@/features/masters';
import { emptyGear } from '../sightings';
import { DmsInput, type SightingsFormPart } from './SightingTable';
import '../voyage-reports.css';
import { ControlledSelect } from '@/features/advance-notices/components/ControlledSelect';

/**
 * 漁場調査等（外国漁船の漁具に関する情報）の明細入力（定時連絡・速報で共用）。
 */
export function GearFindingTable({
  control,
  register,
  defaultDate,
  refSource,
  errors,
  readOnly,
}: {
  control: Control<SightingsFormPart>;
  register: UseFormRegister<SightingsFormPart>;
  defaultDate: string;
  refSource: 'fixed' | 'voyage';
  errors?: FieldErrors<SightingsFormPart>['gearFindings'];
  readOnly?: boolean;
}) {
  const { data: codes } = useCodeValues();
  const rows = useFieldArray({ control, name: 'gearFindings' });
  const values = useWatch({ control, name: 'gearFindings' });
  return (
    <div className="bs-detail-rows">
      {rows.fields.length === 0 && <EmptyState compact icon={<Anchor />} title="漁具の発見情報はありません" description="外国漁船の漁具を発見した場合は「漁具情報を追加」から入力してください。" />}
      {rows.fields.map((f, i) => {
        const p = `gearFindings.${i}` as const;
        const e = (errors?.[i] ?? {}) as Record<string, { message?: string } | undefined>;
        const v = values?.[i];
        return (
          <section key={f.id} className="bs-detail-row is-gear" aria-label={`漁具 ${i + 1}件目`}>
            <header className="bs-detail-row__head">
              <span className="bs-detail-row__no">漁具 {String(i + 1).padStart(2, '0')}</span>
              {v?.zoneNoCalculated != null && <span className="bs-detail-row__calc">機械算出 農林漁区番号：{v.zoneNoCalculated}</span>}
              {!readOnly && (
                <span className="bs-detail-row__tools">
                  <IconButton label="行をコピー" icon={<Copy size={15} />} onClick={() => rows.insert(i + 1, { ...(v ?? emptyGear(defaultDate, refSource)), id: null, zoneNoCalculated: null })} />
                  <IconButton label="行を削除" variant="danger" icon={<Trash2 size={15} />} onClick={() => rows.remove(i)} />
                </span>
              )}
            </header>
            <fieldset className="bs-detail-row__body" disabled={readOnly}>
              <FormField label="発見日" required error={e.foundOn?.message} className="is-w2">
                <DateField {...register(`${p}.foundOn`)} />
              </FormField>
              <FormField label="時刻" className="is-w2" hint="24時間 HH:MM">
                <TextField className="bs-num" placeholder="12:00" {...register(`${p}.foundTime`)} />
              </FormField>
              <FormField label="監視作業水域（通称漁場名）" className="is-w2">
                <TextField {...register(`${p}.monitoringArea`)} />
              </FormField>
              <FormField label="農林漁区番号" className="is-w2">
                <Controller control={control} name={`${p}.zoneNo`} render={({ field }) => <NumberField {...field} width="full" aria-label="農林漁区番号" />} />
              </FormField>
              <FormField label="緯度・経度" className="is-w4" hint="度・分・秒（第3欄は最大4桁）">
                <span className="bs-dms-pair">
                  <DmsInput register={register} base={p} label="緯度" />
                  <DmsInput register={register} base={p} label="経度" />
                </span>
              </FormField>
              <FormField label="漁具種類" className="is-w3">
                <TextField {...register(`${p}.gearType`)} />
              </FormField>
              <FormField label="個数" className="is-w2">
                <TextField {...register(`${p}.gearCount`)} />
              </FormField>
              <FormField label="国籍" className="is-w2">
                <ControlledSelect control={control} name={`${p}.nationality`} placeholder="—" options={(codes?.nationalities ?? []).map((x) => ({ value: x, label: x }))} />
              </FormField>
              <FormField label="MMSI" className="is-w2">
                <TextField className="bs-num" {...register(`${p}.mmsi`)} />
              </FormField>
              <FormField label="写真・参照資料" className="is-w3">
                <span className="bs-checks">
                  <Controller control={control} name={`${p}.hasPhoto`} render={({ field }) => <Checkbox label="写真あり" checked={field.value} onChange={(x) => field.onChange(x.target.checked)} />} />
                  <Controller control={control} name={`${p}.refFixedTime`} render={({ field }) => <Checkbox label="定時" checked={field.value} onChange={(x) => field.onChange(x.target.checked)} />} />
                  <Controller control={control} name={`${p}.refVoyage`} render={({ field }) => <Checkbox label="速報" checked={field.value} onChange={(x) => field.onChange(x.target.checked)} />} />
                </span>
              </FormField>
              <FormField label="備考（漁具の特徴等）" className="is-w6">
                <TextField {...register(`${p}.gearFeatures`)} />
              </FormField>
              <FormField label="特記事項" className="is-w6">
                <TextField {...register(`${p}.specialNotes`)} />
              </FormField>
            </fieldset>
          </section>
        );
      })}
      {!readOnly && (
        <div>
          <Button size="sm" icon={<Plus size={15} />} onClick={() => rows.append(emptyGear(defaultDate, refSource))}>
            漁具情報を追加
          </Button>
        </div>
      )}
    </div>
  );
}
